import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import PageLoader from "../components/common/PageLoader";
import PersonCell from "../components/common/PersonCell";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import EmptyState from "../components/common/EmptyState";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";
import { documentProgressBadge, formatBookingStatus, storedBookingStatus } from "../lib/bookingStatus";
import { appendUniqueById, asArray, readPagination, unwrapData } from "../lib/catalog";
import { fetchCurrentAgent } from "../lib/agentAuth";
import { canWriteBookings, roleFromProfile } from "../lib/agencyAccess";

const PAGE_SIZE = 20;

function formatDate(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function documentBadge(booking) {
  return documentProgressBadge({
    actionRequiredCount: booking.document_action_required_count,
    completeCount: booking.document_complete_count,
    passengerCount: booking.passenger_count || booking.passenger_limit,
  });
}

function visaBadge(booking) {
  const issued = Number(booking.visa_issued_count);
  if (issued > 0) {
    return { value: "issued", label: `${issued} issued` };
  }
  if (Number(booking.visa_processing_count) > 0) {
    return { value: "processing", label: "Processing" };
  }
  return { value: "pending", label: "Pending" };
}

export default function BookingsPage() {
  const [searchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [canCreateBooking, setCanCreateBooking] = useState(false);

  useEffect(() => {
    let alive = true;
    async function run() {
      setLoading(true);
      try {
        const [payload, profile] = await Promise.all([
          agentApi.get("/travel-partner/bookings", {
            params: { limit: PAGE_SIZE, page: 1 },
          }),
          fetchCurrentAgent().catch(() => null),
        ]);
        if (!alive) return;
        setCanCreateBooking(canWriteBookings(roleFromProfile(profile)));
        const rows = asArray(unwrapData(payload));
        const pagination = readPagination(payload);
        setBookings(rows);
        setPage(1);
        setHasNext(pagination.hasNext);
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_BOOKINGS");
      } finally {
        if (alive) setLoading(false);
      }
    }
    run();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setSearch(searchParams.get("q") || "");
  }, [searchParams]);

  async function loadMore() {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const payload = await agentApi.get("/travel-partner/bookings", {
        params: { limit: PAGE_SIZE, page: nextPage },
      });
      const rows = asArray(unwrapData(payload));
      const pagination = readPagination(payload);
      setBookings((current) => appendUniqueById(current, rows, (row) => row.booking_id));
      setPage(nextPage);
      setHasNext(pagination.hasNext);
    } catch (fetchError) {
      showApiError(fetchError, "LOAD_BOOKINGS");
    } finally {
      setLoadingMore(false);
    }
  }

  const filtered = bookings.filter((booking) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [booking.booking_reference, booking.customer_name, booking.booking_status, booking.lifecycle_state, booking.package_name]
      .some((value) => String(value || "").toLowerCase().includes(q));
  });

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Bookings"
          title="Live agent bookings"
          items={[DASHBOARD_CRUMB, { label: "Bookings" }]}
          actions={
            <div className="page-toolbar">
              <input
                type="search"
                className="form-control page-search"
                placeholder="Search loaded bookings"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              {canCreateBooking ? (
                <Link to="/packages" className="btn btn-primary">
                  <iconify-icon icon="solar:add-circle-outline"></iconify-icon>
                  Add booking
                </Link>
              ) : null}
            </div>
          }
        />

        {loading ? <PageLoader message="Loading bookings..." /> : null}

        {!loading && filtered.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <EmptyState
                variant="bookings"
                title={search.trim() && bookings.length > 0
                  ? "No bookings match your search"
                  : "No bookings found for this agent."}
                description={search.trim() && bookings.length > 0
                  ? "Try a different search term, or start a new booking from packages."
                  : "Create your first booking from a live package."}
                action={canCreateBooking ? (
                  <Link to="/packages" className="btn btn-primary">
                    <iconify-icon icon="solar:add-circle-outline"></iconify-icon>
                    Add booking
                  </Link>
                ) : undefined}
              />
            </div>
          </div>
        ) : null}

        {!loading && filtered.length > 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="table-responsive">
              <Table>
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Customer</th>
                    <th>Package</th>
                    <th>Departure</th>
                    <th>Pilgrims</th>
                    <th>Booking status</th>
                    <th>Documents</th>
                    <th>Visa</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((booking) => {
                    const bookingStatus = storedBookingStatus(booking);
                    const documents = documentBadge(booking);
                    const visa = visaBadge(booking);
                    return (
                      <tr key={booking.booking_id}>
                        <td>
                          <Link to={`/bookings/${booking.booking_id}`} className="agent-table-link">
                            {booking.booking_reference}
                          </Link>
                        </td>
                        <td><PersonCell name={booking.customer_name} fallback="Unassigned" /></td>
                        <td>{booking.package_name || "Package"}</td>
                        <td>{formatDate(booking.departure_date || booking.booking_date)}</td>
                        <td>{booking.passenger_count ?? 0}</td>
                        <td>
                          <StatusBadge value={bookingStatus}>
                            {formatBookingStatus(bookingStatus)}
                          </StatusBadge>
                        </td>
                        <td>
                          <StatusBadge value={documents.value}>{documents.label}</StatusBadge>
                        </td>
                        <td>
                          <StatusBadge value={visa.value}>{visa.label}</StatusBadge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              </div>
              {hasNext && !search.trim() ? (
                <div className="text-center mt-3">
                  <button type="button" className="btn btn-outline-primary" disabled={loadingMore} onClick={loadMore}>
                    {loadingMore ? "Loading..." : "Load more"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </AgentLayout>
  );
}
